import { Clock, RefreshCw, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import AuthenticatedLayout from '../components/AuthenticatedLayout'
import { FilingHistoryTable } from '../components/Table'
import { ApiError } from '../utils/api'
import {
  companiesHouseFilingService,
  type ConfirmationStatementFiling,
  type ConfirmationStatementFilingStatus,
} from '../utils/companiesHouseAuthService'
import { companyService } from '../utils/companyService'
import { ct600Service } from '../utils/ct600Service'
import saOnlineService from '../utils/saOnlineService'

const formatUkDate = (value: string | number | Date) =>
  new Date(value).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  })

type HistoryTab =
  | 'corporation-tax'
  | 'self-assessment'
  | 'confirmation-statement'

// Companies House outcome → table badge. Received-but-undecided filings show as "In review".
const csFilingBadge = (status: ConfirmationStatementFilingStatus): 'in-review' | 'accepted' | 'rejected' =>
  status === 'accepted' ? 'accepted' : status === 'rejected' || status === 'failed' ? 'rejected' : 'in-review'

const CS_STATUS_LABEL: Record<ConfirmationStatementFilingStatus, string> = {
  pending: 'Awaiting Companies House decision',
  parked: 'On hold at Companies House',
  accepted: 'Accepted by Companies House',
  rejected: 'Rejected',
  internal_failure: 'Companies House internal failure',
  failed: 'Not received by Companies House',
}

export default function HistoryPage() {
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState<HistoryTab>('corporation-tax')

  // Corporation Tax state
  const [ctFilings, setCtFilings] = useState<any[]>([])
  const [ctLoading, setCtLoading] = useState(true)
  const [ctError, setCtError] = useState<string | null>(null)
  const [ctPagination, setCtPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10,
  })

  // Self Assessment (SA Online) state
  const [rawSaReturns, setRawSaReturns] = useState<any[]>([])
  const [allSaReturns, setAllSaReturns] = useState<any[]>([])
  const [saReturns, setSaReturns] = useState<any[]>([])
  const [saLoading, setSaLoading] = useState(true)
  const [saError, setSaError] = useState<string | null>(null)
  const [saPagination, setSaPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10,
  })

  // Confirmation Statement state: statements filed through BritFiler (with
  // their Companies House outcome), plus the live CS position (last filed /
  // next due / overdue) for companies with nothing filed through BritFiler.
  const [csRows, setCsRows] = useState<any[]>([])
  const [csFilings, setCsFilings] = useState<ConfirmationStatementFiling[]>([])
  const [selectedCsFiling, setSelectedCsFiling] = useState<ConfirmationStatementFiling | null>(null)
  const [csRefreshing, setCsRefreshing] = useState(false)
  const [csLoading, setCsLoading] = useState(true)
  const [csError, setCsError] = useState<string | null>(null)

  useEffect(() => {
    fetchCtFilings()
    fetchSaReturns()
    fetchConfirmationStatements()
  }, [])

  // SA Online pagination
  useEffect(() => {
    const startIndex = (saPagination.currentPage - 1) * saPagination.itemsPerPage
    const endIndex = startIndex + saPagination.itemsPerPage
    setSaReturns(allSaReturns.slice(startIndex, endIndex))
  }, [saPagination.currentPage, allSaReturns])

  // ── Corporation Tax ──────────────────────────────────────────

  const fetchCtFilings = async (page: number = 1) => {
    try {
      setCtLoading(true)
      setCtError(null)
      const response = await ct600Service.listForms({ page, limit: 10 })

      if (response.statusCode === 200 && response.response) {
        const data: any = response.response

        let formsArray: any[] = []
        let paginationData: any = {}

        let unwrappedData = data
        if (data.success && typeof data.data === 'object' && data.data !== null) {
          unwrappedData = data.data
          paginationData = data.pagination || data.data.pagination || {}
        }

        if (Array.isArray(unwrappedData)) {
          formsArray = unwrappedData
        } else if (typeof unwrappedData === 'object' && unwrappedData !== null) {
          formsArray = unwrappedData.forms || unwrappedData.data || unwrappedData.items || unwrappedData.docs || []
          paginationData = unwrappedData.pagination || data.pagination || paginationData
        }

        if (!paginationData || Object.keys(paginationData).length === 0) {
          paginationData = data.pagination || {}
        }

        const transformedData = formsArray.map((form: any) => {
          const status = (form.status || 'draft') as
            | 'draft'
            | 'new'
            | 'submitted'
            | 'accepted'
            | 'rejected'
          const periodEndDate = form.accountingPeriodEnd
            ? new Date(form.accountingPeriodEnd)
            : null
          const filingDeadline = periodEndDate ? new Date(periodEndDate) : null
          // CT600 filing deadline is 12 months after the accounting period ends
          if (filingDeadline) filingDeadline.setMonth(filingDeadline.getMonth() + 12)

          const filedTimestamp = ['submitted', 'accepted', 'rejected'].includes(status)
            ? form.submittedAt || form.acceptedAt || form.updatedAt
            : null

          return {
            id: form._id,
            type: 'CT600',
            company:
              form.companyName ||
              form.sections?.companyInformation?.details?.companyName ||
              form.companyId?.name ||
              'N/A',
            status,
            createdDate: form.createdAt ? formatUkDate(form.createdAt) : '—',
            periodEnd: periodEndDate ? formatUkDate(periodEndDate) : 'N/A',
            dueDate: filingDeadline ? formatUkDate(filingDeadline) : 'N/A',
            filingDate: filedTimestamp ? formatUkDate(filedTimestamp) : '—',
          }
        })

        setCtFilings(transformedData)

        const totalItems = paginationData.totalItems || paginationData.total || transformedData.length
        const currentPage = paginationData.currentPage || paginationData.page || page
        const itemsPerPage = paginationData.limit || 10
        const totalPages = paginationData.totalPages || Math.ceil(totalItems / itemsPerPage)

        setCtPagination({ currentPage, totalPages, totalItems, itemsPerPage })
      }
    } catch (err: any) {
      console.error('Error fetching filings:', err)
      setCtError('Failed to load filing history')
    } finally {
      setCtLoading(false)
    }
  }

  // ── Self Assessment (SA Online — /sa-online/returns) ────────

  const fetchSaReturns = async () => {
    try {
      setSaLoading(true)
      setSaError(null)
      const response = await saOnlineService.listReturns()

      const data: any = response?.response || response
      let returnsArray: any[] = []
      if (Array.isArray(data)) {
        returnsArray = data
      } else if (typeof data === 'object' && data !== null) {
        if (data.success && data.data) {
          returnsArray = Array.isArray(data.data) ? data.data : []
        } else {
          returnsArray = data.returns || data.data || data.items || data.docs || []
        }
      }

      setRawSaReturns(returnsArray)
      const transformedData = returnsArray.map((ret: any) => {
        let status: 'draft' | 'new' | 'submitted' | 'accepted' | 'rejected' | 'in-review' = 'draft'
        const retStatus = ret.status?.toLowerCase()
        if (retStatus === 'submitted' || retStatus === 'filed') {
          status = 'submitted'
        } else if (retStatus === 'accepted') {
          status = 'accepted'
        } else if (retStatus === 'rejected') {
          status = 'rejected'
        } else if (retStatus === 'in-review' || retStatus === 'pending') {
          status = 'in-review'
        } else if (retStatus === 'new') {
          status = 'new'
        } else {
          status = 'draft'
        }

        const taxYear = ret.taxYear || 'N/A'

        const dueDate = ret.updatedAt || ret.createdAt
          ? formatUkDate(ret.updatedAt || ret.createdAt)
          : 'N/A'

        const filedTimestamp =
          status === 'submitted' || status === 'accepted' || status === 'rejected'
            ? ret.submittedAt || ret.filedAt || ret.updatedAt
            : null

        const nino = ret.personalDetails?.nationalInsuranceNumber || ret.nino || ''
        const fullName = [ret.personalDetails?.firstName, ret.personalDetails?.lastName].filter(Boolean).join(' ')
        const company = nino || fullName || ret.uniqueTaxpayerReference || taxYear || 'N/A'

        return {
          id: ret._id,
          type: 'Self Assessment',
          company,
          status,
          createdDate: ret.createdAt ? formatUkDate(ret.createdAt) : '—',
          periodEnd: taxYear !== 'N/A' ? taxYear : 'N/A',
          dueDate,
          filingDate: filedTimestamp ? formatUkDate(filedTimestamp) : '—',
        }
      })

      const totalItems = transformedData.length
      const totalPages = Math.ceil(totalItems / saPagination.itemsPerPage)

      setAllSaReturns(transformedData)
      setSaPagination({
        currentPage: 1,
        totalPages,
        totalItems,
        itemsPerPage: saPagination.itemsPerPage,
      })
    } catch (err: any) {
      console.error('Error fetching SA online returns:', err)
      setSaError('Failed to load self assessment history')
    } finally {
      setSaLoading(false)
    }
  }

  // ── Confirmation Statement (BritFiler filings + live CH position) ──

  const toCsFilingRow = (f: ConfirmationStatementFiling, companies: any[], positions: Map<string, any>) => {
    const company = companies.find((c: any) => c.companyNumber === f.companyNumber)
    const cs = positions.get(f.companyNumber)
    return {
      id: f.id,
      kind: 'filing',
      companyNumber: f.companyNumber,
      type: 'Confirmation Statement',
      company: company?.companyName || company?.name || f.companyName || f.companyNumber,
      status: csFilingBadge(f.status),
      createdDate: formatUkDate(f.submittedAt),
      // The date the statement is made up to.
      periodEnd: formatUkDate(f.reviewDate),
      dueDate: cs?.nextMadeUpTo === f.reviewDate && cs?.nextDue ? formatUkDate(cs.nextDue) : '—',
      filingDate: f.receivedAt ? formatUkDate(f.receivedAt) : '—',
    }
  }

  const fetchConfirmationStatements = async () => {
    try {
      setCsLoading(true)
      setCsError(null)

      const [{ response }, filingsResp] = await Promise.all([
        companyService.getAllCompanies(1, 50),
        // An older backend without the filings endpoint must not blank the tab.
        companiesHouseFilingService.listConfirmationStatementFilings().catch(() => null),
      ])
      const responseData = response as any
      const actual = responseData?.response?.data ?? responseData?.data ?? responseData
      const companies: any[] = Array.isArray(actual)
        ? actual
        : actual?.companies ?? actual?.data ?? actual?.docs ?? actual?.items ?? []
      const filings: ConfirmationStatementFiling[] = filingsResp?.response?.data ?? []

      // Each company's CS position comes from Companies House. One failing
      // company must not blank the whole tab, so failures resolve to null.
      const positions = new Map<string, any>()
      await Promise.all(
        companies.map(async (c: any) => {
          if (!c.companyNumber) return
          try {
            const res = await companyService.getConfirmationStatementDetails(c.companyNumber)
            const details = (res.response as any)?.data ?? res.response
            if (details?.confirmationStatement) positions.set(c.companyNumber, details.confirmationStatement)
          } catch {
            /* skip this company */
          }
        })
      )

      const filedCompanies = new Set(filings.map((f) => f.companyNumber))
      const positionRows = companies
        .filter((c: any) => c.companyNumber && positions.has(c.companyNumber) && !filedCompanies.has(c.companyNumber))
        .map((c: any) => {
          const cs = positions.get(c.companyNumber)
          const today = new Date()
          today.setHours(0, 0, 0, 0)
          const due = cs.nextDue ? new Date(cs.nextDue) : null
          const overdue = !!cs.overdue || (due && !Number.isNaN(due.getTime()) && due < today)

          // Nothing filed through BritFiler, so status reflects the CH position:
          // overdue → rejected styling (red), otherwise the last statement is
          // accepted if one has been filed, else it's still outstanding.
          const status: 'accepted' | 'rejected' | 'draft' = overdue
            ? 'rejected'
            : cs.lastMadeUpTo
              ? 'accepted'
              : 'draft'

          return {
            id: c._id,
            kind: 'position',
            companyNumber: c.companyNumber,
            type: 'Confirmation Statement',
            company: c.companyName || c.name || c.companyNumber,
            status,
            // Straight from Companies House — nothing was "created" here.
            createdDate: '—',
            periodEnd: cs.nextMadeUpTo ? formatUkDate(cs.nextMadeUpTo) : 'N/A',
            dueDate: cs.nextDue ? formatUkDate(cs.nextDue) : 'N/A',
            filingDate: cs.lastMadeUpTo ? formatUkDate(cs.lastMadeUpTo) : '—',
          }
        })

      setCsFilings(filings)
      setCsRows([...filings.map((f) => toCsFilingRow(f, companies, positions)), ...positionRows])
    } catch (err: any) {
      console.error('Error fetching confirmation statements:', err)
      setCsError('Failed to load confirmation statement history')
    } finally {
      setCsLoading(false)
    }
  }

  // Ask the backend for the latest Companies House decision on one filing.
  const refreshCsFiling = async (filing: ConfirmationStatementFiling) => {
    setCsRefreshing(true)
    try {
      const resp = await companiesHouseFilingService.getConfirmationStatementSubmissionStatus(filing.submissionNumber)
      const updated = resp.response?.filing
      if (updated) {
        setSelectedCsFiling(updated)
        setCsFilings((prev) => prev.map((f) => (f.id === updated.id ? updated : f)))
        setCsRows((prev) =>
          prev.map((r) =>
            r.id === updated.id
              ? {
                  ...r,
                  status: csFilingBadge(updated.status),
                  filingDate: updated.receivedAt ? formatUkDate(updated.receivedAt) : r.filingDate,
                }
              : r
          )
        )
        if (updated.status === 'pending') toast.info('Still with Companies House — most filings are decided within 24 hours.')
      }
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : 'Could not check the filing status.')
    } finally {
      setCsRefreshing(false)
    }
  }

  const handleSaPageChange = (newPage: number) => {
    setSaPagination(prev => ({ ...prev, currentPage: newPage }))
  }

  // ── View handlers ────────────────────────────────────────────

  const handleCtView = (record: any) => {
    navigate(`/ct600-filing/${record.id}`)
  }

  const openCsWizard = (companyNumber?: string) => {
    const params = new URLSearchParams()
    if (companyNumber) params.set('companyNumber', companyNumber)
    navigate(`/companies-house-filing${params.toString() ? `?${params.toString()}` : ''}`)
  }

  // A BritFiler filing opens its details; a company position opens the wizard.
  const handleCsView = (record: any) => {
    const row = csRows.find((r: any) => r.id === record.id)
    const filing = row?.kind === 'filing' ? csFilings.find((f) => f.id === row.id) : undefined
    if (filing) {
      setSelectedCsFiling(filing)
      return
    }
    openCsWizard(row?.companyNumber)
  }

  const handleSaView = (record: any) => {
    const raw = rawSaReturns.find((a: any) => a._id === record.id)
    if (!raw) {
      navigate(`/assessment/${record.id}`)
      return
    }
    const params = new URLSearchParams()
    if (raw._id) params.set('saReturnId', raw._id)
    if (raw.assessmentId) params.set('assessmentId', raw.assessmentId)
    // NINO lives in personalDetails.nationalInsuranceNumber
    const nino = raw.personalDetails?.nationalInsuranceNumber || raw.nino || ''
    if (nino) params.set('nino', nino)
    // UTR is uniqueTaxpayerReference
    const utr = raw.uniqueTaxpayerReference || raw.utr || ''
    if (utr) params.set('utr', utr)
    if (raw.taxYear) params.set('taxYear', raw.taxYear)
    params.set('flow', 'sa')
    params.set('fromHistory', 'true')
    navigate(`/self-assessment/filing?${params.toString()}`)
  }

  // ── Shared pagination UI ────────────────────────────────────

  const PaginationControls = ({
    pagination,
    onPageChange,
    position = 'top',
  }: {
    pagination: typeof ctPagination
    onPageChange: (page: number) => void
    position?: 'top' | 'bottom'
  }) => (
    <div className={`flex flex-col sm:flex-row items-center ${position === 'top' ? 'sm:justify-between' : 'justify-center'} gap-2 ${position === 'bottom' ? 'mt-4' : 'mb-4'}`}>
      {position === 'top' && (
        <p className='text-sm text-gray-600'>
          {pagination.totalItems} record{pagination.totalItems !== 1 ? 's' : ''} found
        </p>
      )}
      <div className='flex items-center gap-2'>
        <button
          onClick={() => onPageChange(pagination.currentPage - 1)}
          disabled={pagination.currentPage === 1}
          className='px-4 py-2 text-sm border border-gray-300 text-black rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors font-medium'
        >
          Previous
        </button>
        <span className='text-sm text-gray-600 px-2'>
          Page {pagination.currentPage} of {pagination.totalPages}
        </span>
        <button
          onClick={() => onPageChange(pagination.currentPage + 1)}
          disabled={pagination.currentPage === pagination.totalPages}
          className='px-4 py-2 text-sm border border-gray-300 text-black rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors font-medium'
        >
          Next
        </button>
      </div>
    </div>
  )

  // ── Shared loading / error / empty states ───────────────────

  const LoadingState = ({ label }: { label: string }) => (
    <div className='flex items-center justify-center py-12'>
      <div className='text-center'>
        <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto'></div>
        <p className='mt-4 text-gray-600'>Loading {label}...</p>
      </div>
    </div>
  )

  const ErrorState = ({ message }: { message: string }) => (
    <div className='bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md text-sm'>
      {message}
    </div>
  )

  const EmptyState = ({ label }: { label: string }) => (
    <div className='flex flex-col items-center justify-center py-12'>
      <p className='text-gray-500 text-sm'>No {label} records found</p>
      <p className='text-gray-400 text-xs mt-2'>Create a new filing to get started</p>
    </div>
  )

  return (
    <AuthenticatedLayout activeTab='filings'>
      <div className='grid grid-cols-1 gap-6'>
        <div className='p-6 bg-white rounded-2xl shadow-sm border-0'>
          {/* Header */}
          <div className='flex items-start gap-2 mb-6'>
            <Clock className='w-5 h-5 text-gray-700 mt-1' />
            <h3 className='text-lg font-bold text-gray-900'>Filing History</h3>
          </div>

          {/* Tabs */}
          <div className='flex border-b border-gray-200 mb-6 overflow-x-auto scrollbar-hide'>
            {(
              [
                ['corporation-tax', 'Corporation Tax Filing'],
                ['self-assessment', 'Self Assessment Filing'],
                ['confirmation-statement', 'Confirmation Statement'],
              ] as [HistoryTab, string][]
            ).map(([key, label]) => (
              <button
                key={key}
                onClick={() => setActiveTab(key)}
                className={`px-5 py-3 text-sm font-medium transition-colors relative whitespace-nowrap ${
                  activeTab === key
                    ? 'text-red-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {label}
                {activeTab === key && (
                  <span className='absolute bottom-0 left-0 right-0 h-0.5 bg-red-500 rounded-full' />
                )}
              </button>
            ))}
          </div>

          {/* Corporation Tax Tab Content */}
          {activeTab === 'corporation-tax' && (
            <>
              {ctLoading ? (
                <LoadingState label='corporation tax filing history' />
              ) : ctError ? (
                <ErrorState message={ctError} />
              ) : ctFilings.length === 0 ? (
                <EmptyState label='corporation tax filing' />
              ) : (
                <div>
                  <PaginationControls
                    pagination={ctPagination}
                    onPageChange={(page) => fetchCtFilings(page)}
                    position='top'
                  />
                  <div className='border border-gray-200 rounded-lg overflow-hidden'>
                    <FilingHistoryTable data={ctFilings} onView={handleCtView} />
                  </div>
                  <PaginationControls
                    pagination={ctPagination}
                    onPageChange={(page) => fetchCtFilings(page)}
                    position='bottom'
                  />
                </div>
              )}
            </>
          )}

          {/* Self Assessment Tab Content (SA Online) */}
          {activeTab === 'self-assessment' && (
            <>
              {saLoading ? (
                <LoadingState label='self assessment history' />
              ) : saError ? (
                <ErrorState message={saError} />
              ) : saReturns.length === 0 ? (
                <EmptyState label='self assessment' />
              ) : (
                <div>
                  <PaginationControls
                    pagination={saPagination}
                    onPageChange={handleSaPageChange}
                    position='top'
                  />
                  <div className='border border-gray-200 rounded-lg overflow-hidden'>
                    <FilingHistoryTable data={saReturns} onView={handleSaView} />
                  </div>
                  <PaginationControls
                    pagination={saPagination}
                    onPageChange={handleSaPageChange}
                    position='bottom'
                  />
                </div>
              )}
            </>
          )}

          {/* Confirmation Statement Tab Content */}
          {activeTab === 'confirmation-statement' && (
            <>
              {csLoading ? (
                <LoadingState label='confirmation statement history' />
              ) : csError ? (
                <ErrorState message={csError} />
              ) : csRows.length === 0 ? (
                <EmptyState label='confirmation statement filing' />
              ) : (
                <div className='space-y-4'>
                  {selectedCsFiling && (
                    <CsFilingDetails
                      filing={selectedCsFiling}
                      refreshing={csRefreshing}
                      onRefresh={() => refreshCsFiling(selectedCsFiling)}
                      onFileAgain={() => openCsWizard(selectedCsFiling.companyNumber)}
                      onClose={() => setSelectedCsFiling(null)}
                    />
                  )}
                  <div className='border border-gray-200 rounded-lg overflow-hidden'>
                    <FilingHistoryTable data={csRows} onView={handleCsView} />
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </AuthenticatedLayout>
  )
}

// Details of one Confirmation Statement filed through BritFiler: where it is
// with Companies House, and why if it was rejected.
function CsFilingDetails({
  filing,
  refreshing,
  onRefresh,
  onFileAgain,
  onClose,
}: {
  filing: ConfirmationStatementFiling
  refreshing: boolean
  onRefresh: () => void
  onFileAgain: () => void
  onClose: () => void
}) {
  const outstanding = filing.status === 'pending' || filing.status === 'parked' || filing.status === 'internal_failure'
  const canRefile = filing.status === 'rejected' || filing.status === 'failed'
  const tone =
    filing.status === 'accepted'
      ? 'border-emerald-200 bg-emerald-50'
      : canRefile
        ? 'border-red-200 bg-red-50'
        : 'border-blue-200 bg-blue-50'

  return (
    <div className={`rounded-lg border p-4 text-sm ${tone}`}>
      <div className='flex items-start justify-between gap-4'>
        <div>
          <p className='font-semibold text-gray-900'>
            {filing.companyName || filing.companyNumber} — {CS_STATUS_LABEL[filing.status]}
          </p>
          <p className='mt-0.5 text-xs text-gray-600'>
            Submission {filing.submissionNumber} · statement date {formatUkDate(filing.reviewDate)} · submitted{' '}
            {formatUkDate(filing.submittedAt)}
            {filing.completedAt ? ` · decided ${formatUkDate(filing.completedAt)}` : ''}
            {filing.testMode ? ' · test submission' : ''}
          </p>
        </div>
        <button type='button' onClick={onClose} aria-label='Close' className='rounded p-1 text-gray-500 hover:bg-white/60'>
          <X className='h-4 w-4' />
        </button>
      </div>

      {filing.statusMessage && <p className='mt-3 text-gray-700'>{filing.statusMessage}</p>}

      {filing.rejections.length > 0 && (
        <ul className='mt-3 list-disc space-y-1 pl-5 text-red-800'>
          {filing.rejections.map((r, i) => (
            <li key={`${r.code}-${i}`}>
              {r.message} <span className='text-xs text-red-500'>({r.code})</span>
            </li>
          ))}
        </ul>
      )}

      {filing.examinerComment && (
        <p className='mt-3 text-gray-700'>
          <span className='font-medium'>Companies House examiner:</span> {filing.examinerComment}
        </p>
      )}

      {(outstanding || canRefile) && (
        <div className='mt-4 flex flex-wrap gap-2'>
          {outstanding && (
            <button
              type='button'
              onClick={onRefresh}
              disabled={refreshing}
              className='inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50'
            >
              <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? 'animate-spin' : ''}`} />
              Check status
            </button>
          )}
          {canRefile && (
            <button
              type='button'
              onClick={onFileAgain}
              className='rounded-lg bg-gray-900 px-4 py-2 text-xs font-semibold text-white hover:bg-gray-800'
            >
              Fix and file again
            </button>
          )}
        </div>
      )}
    </div>
  )
}
